import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import { getYear, isWithinInterval, isSameDay, startOfWeek, endOfWeek, getISOWeek, startOfDay } from 'date-fns';
import { useRef, useState, useMemo, useCallback } from 'react';
import 'regenerator-runtime/runtime';
import { useAsyncDebounce } from 'react-table';
import { useLocale, languageToLocale } from '../../utils/locale.js';
import { getWeeksClassName } from '../BaseDatePicker/utils/getWeeksClassName.js';
import { getDaysClassName } from '../BaseDatePicker/utils/getDaysClassName.js';
import 'date-fns/format';
import { getWeekStartOn } from '../BaseDatePicker/utils/getWeekDetails.js';
import { useWeekPicker } from '../BaseDatePicker/hooks/useWeekPicker.js';
import '../BaseDatePicker/components/DayContent.js';
import '../BaseDatePicker/components/BaseDatePickerPopper/BaseDatePickerPopper.js';
import '../BaseDatePicker/components/DatePickerInput/DatePickerInput.js';
import BaseDatePicker from '../BaseDatePicker/BaseDatePicker.js';
import { getCustomRangeClassName } from '../BaseDatePicker/utils/getCustomRangeClassName.js';
import DatePickerHeader from './components/DatePickerHeader.js';

const DEFAULT_MIN_YEAR = new Date(`04/15/${getYear(new Date()) - 5}`);
const DEFAULT_MAX_YEAR = new Date(`12/15/${getYear(new Date()) + 5}`);
const DatePicker = (_a) => {
    var { onChange, value = null, isWeekPicker, onWeekSelect, headerLabels = {}, weekStartsOn = 0, headerLocale, labelFormat = 'EEEE, d MMM yyyy', maxDate = DEFAULT_MAX_YEAR, minDate = DEFAULT_MIN_YEAR, monthFormat = 'LLLL', today, hideYearDropdown, customRangeSelect } = _a, rest = __rest(_a, ["onChange", "value", "isWeekPicker", "onWeekSelect", "headerLabels", "weekStartsOn", "headerLocale", "labelFormat", "maxDate", "minDate", "monthFormat", "today", "hideYearDropdown", "customRangeSelect"]);
    const pickerRef = useRef(null);
    const week = useWeekPicker(value, weekStartsOn);
    const customRange = customRangeSelect ? customRangeSelect(value) : undefined;
    const [hoveredDate, setHoveredDate] = useState();
    const hoveredRange = useMemo(() => (hoveredDate && customRangeSelect ? customRangeSelect(hoveredDate) : undefined), [hoveredDate, customRangeSelect]);
    const locale = useLocale();
    const localeInstance = headerLocale ? languageToLocale(headerLocale) : locale;
    const isDateSelected = useCallback(date => {
        if (customRange) {
            const [start, end] = customRange;
            return !!start && !!end && isWithinInterval(date, { start, end });
        }
        if (isWeekPicker) {
            const { start, end } = week;
            return !!start && !!end && isWithinInterval(date, { start, end });
        }
        return !!value && isSameDay(value, date);
    }, [customRange, isWeekPicker, week, value]);
    const getDayClassName = useCallback(date => {
        if (customRange || hoveredRange) {
            return getCustomRangeClassName(date, customRange, hoveredDate, hoveredRange);
        }
        if (isWeekPicker) {
            const weekStart = getWeekStartOn({ date: hoveredDate, weekStartsOn });
            return getWeeksClassName(date, week, week, weekStart, hoveredDate);
        }
        return getDaysClassName(date, [value, value], hoveredDate);
    }, [isWeekPicker, weekStartsOn, hoveredDate, week, value, customRange, hoveredRange]);
    const onChangeInternal = useCallback((selectedValue, e) => {
        if (customRangeSelect) {
            const selectedCustomRange = customRangeSelect(selectedValue);
            if (selectedCustomRange) {
                onChange(selectedCustomRange[0], e);
                return;
            }
        }
        if (isWeekPicker && selectedValue) {
            const weekStart = getWeekStartOn({ date: selectedValue, weekStartsOn });
            const start = startOfWeek(selectedValue, { weekStartsOn: weekStart });
            const range = [start, endOfWeek(selectedValue, { weekStartsOn: weekStart })];
            const weekNumber = getISOWeek(selectedValue);
            onChange(start, e);
            onWeekSelect === null || onWeekSelect === void 0 ? void 0 : onWeekSelect(range, weekNumber);
        }
        onChange(selectedValue ? startOfDay(selectedValue) : null, e);
    }, [onWeekSelect, onChange, isWeekPicker, weekStartsOn, customRangeSelect]);
    const onChangeDebounced = useAsyncDebounce(onChangeInternal, 300);
    const onChangeCallback = useCallback((value, e) => {
        if ((e === null || e === void 0 ? void 0 : e.type) === 'click') {
            onChangeInternal(value, e);
        }
        else {
            onChangeDebounced(value, e);
        }
    }, [onChangeInternal, onChangeDebounced]);
    return (jsx(BaseDatePicker, Object.assign({}, rest, { ref: pickerRef, selected: value, weekStartsOn: weekStartsOn, onChange: onChangeCallback, onDayMouseEnter: setHoveredDate, onMonthMouseLeave: () => setHoveredDate(undefined), dayClassName: getDayClassName, maxDate: maxDate, minDate: minDate, today: today, isWeekPicker: isWeekPicker, customRange: customRange || hoveredRange, isDateSelected: isDateSelected, inputProps: { labelFormat }, renderCustomHeader: params => (jsx(DatePickerHeader, Object.assign({ locale: localeInstance, maxDate: maxDate, minDate: minDate, monthFormat: monthFormat, hideYearDropdown: hideYearDropdown }, params, headerLabels))) })));
};

export { DatePicker as default };
//# sourceMappingURL=DatePicker.js.map
